import { isPublicRoute } from "@/app/providers/constants";
import { logout } from "@/components/logout-button";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from "@/components/ui/card";
import { useOnboardingSteps } from "@/hooks/use-onboarding-steps";
import { cn } from "@/utils/utils";
import { useMutation } from "@tanstack/react-query";
import { ChevronLeft, Headset, LogOut } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { ReactNode } from "react";
import { NO_BACK_BUTTON_PATHS } from "../constants";
import { OnboardingStepper } from "./onboarding-stepper";
import { onboardingProps, useOnboardingStore } from "@/store/onboardingStore";
import { isOnboardingLoadingStep } from "@jurni/schemas";

const LoadingContent = () => {
  return (
    <div className="flex h-full w-full max-w-[500px] flex-1 flex-col">
      <div className="mb-10 h-7 w-2/3 animate-pulse rounded bg-muted" />
      <div className="mb-2 h-5 w-1/2 animate-pulse rounded bg-muted" />
      <div className="flex flex-col gap-3">
        {[1, 2].map((i) => (
          <div
            key={i}
            className="flex animate-pulse space-x-4 rounded-lg bg-muted p-5"
          >
            <div className="h-5 w-5 rounded-full bg-muted-foreground/30" />
            <div className="flex flex-col gap-1">
              <div className="h-4 w-32 rounded bg-muted-foreground/30" />
              <div className="h-4 w-40 rounded bg-muted-foreground/20" />
            </div>
          </div>
        ))}
      </div>
      <div className="mt-8 h-10 w-full animate-pulse rounded bg-muted" />
    </div>
  );
};

interface LeftContentProps {
  content: ReactNode;
}

export const LeftContent = ({ content }: LeftContentProps) => {
  const router = useRouter();
  const pathname = usePathname();
  const publicRoute = isPublicRoute(pathname);
  const { goBack, isOnboardingAuthorizedPath, canGoBack } =
    useOnboardingSteps();
  const showBackButton = canGoBack && !NO_BACK_BUTTON_PATHS.includes(pathname);
  const currentStepName = useOnboardingStore(onboardingProps.currentStepName);
  const isOnboardingLoading =
    currentStepName && isOnboardingLoadingStep(currentStepName);

  const { mutate: handleLogout, isPending: isLogoutPending } = useMutation({
    mutationFn: () => logout(),
  });

  const handleBack = () => {
    if (isOnboardingAuthorizedPath) {
      goBack();
    } else {
      router.back();
    }
  };

  return (
    <Card
      className={cn(
        "flex min-h-[calc(100vh-24px)] w-full flex-col",
        "items-center rounded-2xl px-4 pb-14 pt-14 lg:px-10",
      )}
    >
      <div className="flex h-full w-full max-w-[500px] flex-1 flex-col">
        <CardHeader className="flex flex-row justify-between space-y-0 pb-14 pt-0">
          <Image src="/new-jurni-logo.svg" alt="Jurni" width={80} height={29} />
          {!publicRoute && (
            <Button
              variant="link"
              className="hover:text-muted-foreground-hover gap-2 text-muted-foreground"
              onClick={() => handleLogout()}
              disabled={isLogoutPending}
            >
              <LogOut size={16} />
              Log Out
            </Button>
          )}
        </CardHeader>
        <CardContent
          key={pathname}
          className="flex min-h-0 flex-1 flex-col py-0 duration-300 animate-in fade-in"
        >
          {isOnboardingLoading ? <LoadingContent /> : content}
        </CardContent>
        <CardFooter className="mt-auto flex h-16 items-end justify-between gap-2 pb-0 text-[13px]">
          {showBackButton && (
            <Button
              variant="link"
              className="hover:text-muted-foreground-hover gap-1 text-muted-foreground"
              onClick={handleBack}
            >
              <ChevronLeft size={16} />
              Back
            </Button>
          )}
          {publicRoute && (
            <div
              className={cn(
                "flex items-center gap-8",
                showBackButton ? "ml-auto" : "mx-auto",
              )}
            >
              <Link
                className="flex items-center gap-1 text-muted-foreground hover:underline"
                href="mailto:info@getjurni.ai"
              >
                <Headset className="text-muted-foreground/60" size={12} />
                Contact Us
              </Link>
              <Link
                className="flex items-center gap-1 text-muted-foreground hover:underline"
                href="https://getjurni.ai/privacy-policy"
                target="_blank"
              >
                Privacy Policy
              </Link>
            </div>
          )}
          <OnboardingStepper className="ml-auto" />
        </CardFooter>
      </div>
    </Card>
  );
};
